import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base.service';
import { PaginationParams } from '../interfaces/user.interface';
import {
  InsuranceContractsResponse,
  InsuranceContract,
  InsuranceService as InsuranceServiceInterface,
  ContractMessagesResponse,
  InsuranceMessage,
} from '../interfaces/insurance.interface';

export interface TransmitInsuranceContractDto {
  // Interface générique selon le swagger
  [key: string]: any;
}

export interface TransmitInsuranceDocumentsDto {
  // Interface générique selon le swagger
  [key: string]: any;
}

export interface InsuranceDocument {
  name: string;
  extension: string;
  patientExternalid: string;
  documentTypeCode: string;
  content: string;
  presignedUrl?: {
    url: string;
    expires_in: number;
  };
}

export interface HealthPro {
  eyoneExternalId: string;
  name: string;
}

export interface CreateInsuranceMessageDto {
  content: string;
  messageType:
    | 'CARE_REQUEST'
    | 'INSURANCE_IDENTIFICATION_NUM_MAJ'
    | 'IDENTIFICATION_PHOTO_MAJ'
    | 'QUESTION'
    | 'QUOTE'
    | 'REFUND_REQUEST';
  messageActorType: 'INSURER' | 'HEALTH_PROFESSIONAL' | 'PATIENT' | 'INSURED';
  destinaryType:
    | 'INSURER'
    | 'HEALTH_PROFESSIONAL'
    | 'PATIENT'
    | 'INSURED'
    | 'RADIOLOGY'
    | 'PHYSICIAN'
    | 'PHARMACY'
    | 'MEDICAL_STRUCTURE'
    | 'LABORATORY';
  beneficiaryPasspport?: string;
  documents?: string[]; // Tableau d'IDs de documents (_id)
  healthProEyoneExternalId?: string;
  healthProEyoneName?: string;
  healthProType?: string;
}

export interface CreateRefundRequestDto {
  beneficiaryEyoneExternalId: string;
  healthProEyoneExternalId?: string;
  healthProName: string;
  healthProType?: string;
}

export interface CreateInsuranceMessageResponseDto {
  content: string;
}

export interface TransmitMessageResponseDto {
  eyoneExternalId: string;
  content: string;
  documentFromUpload?: any;
}

@Injectable({
  providedIn: 'root',
})
export class InsuranceService extends BaseService {
  constructor(http: HttpClient) {
    super(http);
  }

  // Obtenir les types de messages
  getMessageTypes(): Observable<any> {
    return this.http.get(`${this.baseUrl}/insurances/message-types`, {
      headers: this.getHeaders(),
    });
  }

  // Obtenir les prestataires
  getProviders(): Observable<any> {
    return this.http.get(`${this.baseUrl}/insurances/prestataires`, {
      headers: this.getHeaders(),
    });
  }

  // Obtenir les prestataires non conventionnés
  getNonConventionnedProviders(insurerExternalId: string, searchTerm: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/insurances/prestataires?eyoneExternalId=${insurerExternalId}&searchName=${searchTerm}`, {
      headers: this.getHeaders(),
    });
  }

  // Obtenir les contrats utilisateur avec typage
  getUserContracts(): Observable<InsuranceContractsResponse> {
    return this.http.get<InsuranceContractsResponse>(
      `${this.baseUrl}/insurances/contracts`,
      {
        headers: this.getAuthHeaders(),
      }
    );
  }

  // Obtenir les détails d'un contrat
  getContractDetails(contractId: string): Observable<InsuranceContract> {
    return this.http.get<InsuranceContract>(
      `${this.baseUrl}/insurances/contracts/${contractId}`,
      {
        headers: this.getHeaders(),
      }
    );
  }

  getInsuranceDocuments(contractId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/insurances/messages/${contractId}/documents`,
      { headers: this.getHeaders() }
    );
  }

  // Obtenir les bénéficiaires d'un contrat
  getContractBeneficiaries(contractId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/insurances/contracts/${contractId}/beneficiaries`,
      { headers: this.getHeaders() }
    );
  }

  // Transmettre un contrat d'assurance
  transmitContract(
    contractData: TransmitInsuranceContractDto
  ): Observable<any> {
    return this.http.post(`${this.baseUrl}/insurances/transmit`, contractData, {
      headers: this.getHeaders(),
    });
  }

  sendResponse(messageId: string, content: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/insurances/messages/${messageId}/responses`,
      { content },
      { headers: this.getHeaders() }
    );
  }

  // Transmettre des documents d'assurance
  transmitDocuments(
    documentsData: TransmitInsuranceDocumentsDto
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/insurances/transmit/documents`,
      documentsData,
      { headers: this.getHeaders() }
    );
  }

  // Créer un message d'assurance
  createMessage(
    contractId: string,
    messageData: CreateInsuranceMessageDto
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/insurances/contracts/${contractId}/messages`,
      messageData,
      { headers: this.getHeaders() }
    );
  }

  // Créer un message d'assurance avec fichiers (form-data)
  createMessageWithFiles(
    contractId: string,
    messageData: CreateInsuranceMessageDto,
    files?: File[]
  ): Observable<any> {
    const formData = new FormData();

    // Ajouter les données du message
    formData.append('content', messageData.content);
    formData.append('messageType', messageData.messageType);
    formData.append('messageActorType', messageData.messageActorType);
    formData.append('destinaryType', messageData.destinaryType);

    if (messageData.beneficiaryPasspport) {
      formData.append('beneficiaryPasspport', messageData.beneficiaryPasspport);
    }

    if (messageData.healthProEyoneExternalId) {
      formData.append(
        'healthProEyoneExternalId',
        messageData.healthProEyoneExternalId
      );
    }

    if (messageData.healthProEyoneName) {
      formData.append('healthProEyoneName', messageData.healthProEyoneName);
    }

    if (messageData.healthProType) {
      formData.append('healthProType', messageData.healthProType);
    }

    // Ajouter les documents si présents (tableau d'IDs)
    if (messageData.documents && messageData.documents.length > 0) {
      messageData.documents.forEach((documentId, index) => {
        formData.append(`documents[${index}]`, documentId);
      });
    }

    // Ajouter les fichiers
    if (files && files.length > 0) {
      files.forEach((file, index) => {
        formData.append('files', file);
      });
    }

    // Pour form-data, ne pas définir Content-Type car le navigateur le fait automatiquement
    // avec la boundary appropriée
    const headers = new HttpHeaders();

    return this.http.post(
      `${this.baseUrl}/insurances/contracts/${contractId}/messages`,
      formData,
      { headers }
    );
  }

  // Obtenir les messages d'un contrat
  getContractMessages(
    contractId: string,
    params: PaginationParams = {}
  ): Observable<ContractMessagesResponse> {
    const httpParams = this.createParams(params);
    return this.http.get<ContractMessagesResponse>(
      `${this.baseUrl}/insurances/contracts/${contractId}/messages`,
      {
        headers: this.getAuthHeaders(),
        params: httpParams,
      }
    );
  }

  // Obtenir les documents d'un contrat
  getContractDocuments(
    contractId: string,
    params: PaginationParams = {}
  ): Observable<any> {
    const httpParams = this.createParams(params);
    return this.http.get(
      `${this.baseUrl}/insurances/contracts/${contractId}/documents`,
      {
        headers: this.getAuthHeaders(),
        params: httpParams,
      }
    );
  }

  // Créer une réponse à un message
  createMessageResponse(
    messageId: string,
    responseData: CreateInsuranceMessageResponseDto
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/insurances/messages/${messageId}/responses`,
      responseData,
      { headers: this.getAuthHeaders() }
    );
  }

  // Obtenir les réponses d'un message
  getMessageResponses(messageId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/insurances/messages/${messageId}/responses`,
      { headers: this.getAuthHeaders() }
    );
  }

  // Marquer une réponse comme lue
  markResponseAsRead(responseId: string): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/insurances/messages/responses/${responseId}/read`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  // Transmettre une réponse de message
  transmitMessageResponse(
    responseData: TransmitMessageResponseDto
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/insurances/transmit/response`,
      responseData,
      { headers: this.getHeaders() }
    );
  }
}
